import { motion, AnimatePresence } from 'framer-motion'
import { useLocation } from 'react-router-dom'
import { ReactNode, useRef, useEffect } from 'react'

interface PageTransitionProps {
  children: ReactNode
}

// Define menu order (left to right)
const MENU_ORDER = [
  '/news',
  '/listings',
  '/groups',
  '/my-listings',
  '/messages',
  '/team',
  '/admin',
  '/profile',
  '/login'
]

// Helper to get base path (remove params and hash)
const getBasePath = (pathname: string): string => {
  // For dynamic routes like /listings/:id, map to parent
  if (pathname.startsWith('/listings/') && pathname !== '/listings') {
    return '/listings'
  }
  if (pathname.startsWith('/groups/') && pathname !== '/groups') {
    return '/groups'
  }
  if (pathname.startsWith('/messages/') && pathname !== '/messages') {
    return '/messages'
  }
  return pathname
}

// Hook to get previous value
const usePrevious = <T,>(value: T): T | undefined => {
  const ref = useRef<T>()
  useEffect(() => {
    ref.current = value
  }, [value])
  return ref.current
}

const PageTransition = ({ children }: PageTransitionProps) => {
  const location = useLocation()
  const currentPath = getBasePath(location.pathname)
  const prevPath = usePrevious(currentPath)
  // Simple horizontal slide for all routes (fallback to vertical logic removed for stability)
  const shouldSlide = currentPath !== prevPath
  const isFromRight = (() => {
    if (!prevPath) return true
    const currentIdx = MENU_ORDER.indexOf(currentPath)
    const prevIdx = MENU_ORDER.indexOf(prevPath)
    if (currentIdx === -1 || prevIdx === -1) return true
    return currentIdx > prevIdx
  })()

  return (
    <div className="relative w-full flex flex-col">
      <AnimatePresence mode="sync" initial={false}>
        <motion.div
          key={location.pathname}
          initial={{ x: shouldSlide ? (isFromRight ? '100%' : '-100%') : 0, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: shouldSlide ? (isFromRight ? '-100%' : '100%') : 0, opacity: 0 }}
          transition={{ x: { duration: shouldSlide ? 0.4 : 0, ease: 'easeInOut' }, opacity: { duration: 0.3 } }}
          className="w-full flex flex-col"
          style={{ backfaceVisibility: 'hidden' }}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

export default PageTransition
